import { useId, useMemo } from 'react';
import Select from 'react-select';
import { scopeMultiSelectOptions } from '../lib/oidcScopeForm';

const scopeSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.isFocused ? '#0d6efd' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 1px #0d6efd' : 'none',
    '&:hover': { borderColor: '#adb5bd' },
    backgroundColor: state.isDisabled ? '#f8f9fa' : '#fff',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    zIndex: 50,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    cursor: 'pointer',
    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : '#fff',
    color: state.isSelected ? '#fff' : '#212529',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#cfe2ff',
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#084298',
    fontSize: '0.8125rem',
    fontWeight: 600,
  }),
  multiValueRemove: (base, props) => ({
    ...base,
    ...(props.data.value === 'openid'
      ? { display: 'none' }
      : {
          ':hover': { backgroundColor: '#9ec5fe', color: '#052c65' },
        }),
  }),
  placeholder: (base) => ({ ...base, fontSize: '0.875rem', color: '#6c757d' }),
  input: (base) => ({ ...base, fontSize: '0.875rem' }),
};

/**
 * Multi-select for OIDC scopes (searchable chips). `openid` cannot be removed.
 */
export default function OidcScopeSelect({ id, value, onChange, disabled }) {
  const rid = useId();
  const instanceId = (id || `oidc-scope-${rid}`).replace(/[^a-zA-Z0-9_-]/g, '');

  const options = useMemo(
    () => scopeMultiSelectOptions(value).map((v) => ({ value: v, label: v })),
    [value]
  );

  const selected = useMemo(() => value.map((v) => ({ value: v, label: v })), [value]);

  return (
    <Select
      inputId={id}
      instanceId={instanceId}
      isMulti
      options={options}
      value={selected}
      onChange={(newVal) => {
        const raw = (newVal || []).map((o) => o.value);
        const next = raw.includes('openid') ? raw : ['openid', ...raw];
        onChange(next);
      }}
      isDisabled={disabled}
      isClearable={false}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      placeholder="Search or select scopes…"
      classNamePrefix="oidc-scope-select"
      styles={scopeSelectStyles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
      noOptionsMessage={() => 'No scopes'}
    />
  );
}
